import type { LucideIcon } from 'lucide-react';
import { ArrowLeftIcon, ChevronsUpDownIcon, UploadIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { NavLink } from 'react-router';

import { ProjectIcon } from '@/components/project-icon';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import { assetPath, cn } from '@/lib/utils';

/*
 * The app sidebar's parts and their geometry: the pinned header, the
 * scrolling body, the pinned footer, and every control that shows in the
 * folded icon rail. AppSidebar supplies the data and the menus; these own the
 * layout, so the frontend-only layout suite can hold the sidebar to it with
 * the same markup the app renders.
 *
 * Every row puts its content on one left edge, 16px in: interactive rows are
 * inset 8px and pad 8px, static text sits in an 8px inset with 8px padding.
 */

export interface NavItem {
  readonly href: string;
  readonly icon: LucideIcon;
  readonly label: string;
  readonly activeHrefs?: readonly string[];
  readonly match?: 'exact' | 'none' | 'prefix';
  /**
   * Folio how-to target id (data-folio-target). Mirrors `RegisteredPage`
   * entryPath testIds so the lookup tool can point Sean at the live affordance.
   * See `apps/web/src/folio/page-registry.ts`.
   */
  readonly folioTargetId?: string;
  /**
   * Set while this capability lane has not been announced in this browser
   * (docs/design/ia-shape-brief.md §7). The lane plays the one-shot pop-in and
   * accent flash on mount and calls this when the flash ends, so the caller can
   * record the announcement and later mounts render the lane like its peers.
   */
  readonly onPopInEnd?: () => void;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * The EY-Parthenon tagline lockup and the product name; folded, the stacked
 * mark. The lockup keeps the 176px width its 170px brand minimum allows, and a
 * third of its E height clear around it.
 */
function SidebarBrand() {
  return (
    <>
      <NavLink to="/" className="block px-2 pt-1 group-data-[collapsible=icon]:hidden">
        <img
          src={assetPath('/logos/eyp-logo-tagline-light.svg')}
          alt="EY-Parthenon"
          className="h-auto w-44 dark:hidden"
        />
        <img
          src={assetPath('/logos/eyp-logo-tagline-dark.svg')}
          alt="EY-Parthenon"
          className="hidden h-auto w-44 dark:block"
        />
        <p className="mt-2.5 border-t border-border/60 pt-2 font-heading text-sm font-bold tracking-tight text-foreground">
          Contract Analyzer
        </p>
      </NavLink>
      <div className="hidden items-center justify-center py-1.5 group-data-[collapsible=icon]:flex">
        <NavLink to="/">
          <img
            src={assetPath('/logos/eyp-logo-stacked-dark.svg')}
            alt="EY-Parthenon"
            className="h-auto w-8 dark:hidden"
          />
          <img
            src={assetPath('/logos/eyp-logo-stacked-light.svg')}
            alt="EY-Parthenon"
            className="hidden h-auto w-8 dark:block"
          />
        </NavLink>
      </div>
    </>
  );
}

/** The workspace switcher's trigger: the workspace's name and a chevron. */
export function SidebarWorkspaceButton({
  workspaceName,
  ...props
}: Omit<ComponentProps<'button'>, 'children'> & { readonly workspaceName: string }) {
  return (
    <button
      type="button"
      aria-label="Switch workspace"
      title={`Workspace: ${workspaceName}`}
      className="group/ws flex h-7 w-full items-center justify-between gap-2 rounded-md px-2 text-left transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
      {...props}
    >
      <span className="truncate text-sm font-medium text-foreground">{workspaceName}</span>
      <ChevronsUpDownIcon className="size-3.5 shrink-0 text-muted-foreground/70 transition-colors group-hover/ws:text-foreground" />
    </button>
  );
}

/** The workspace's name where there is no other workspace to switch to. */
export function SidebarWorkspaceName({ workspaceName }: { readonly workspaceName: string }) {
  return (
    <p className="truncate px-2 text-sm leading-7 font-medium text-foreground">
      <span className="sr-only">Workspace: </span>
      {workspaceName}
    </p>
  );
}

/** The open project, with the way back to the workspace's projects. */
export function SidebarProjectRow({
  backHref,
  displayName,
  iconUrl,
}: {
  readonly backHref: string;
  readonly displayName: string;
  readonly iconUrl: ComponentProps<typeof ProjectIcon>['iconUrl'];
}) {
  return (
    <div className="flex h-7 items-center gap-2 px-1">
      <Button
        asChild
        variant="ghost"
        size="icon"
        className="size-6 text-muted-foreground"
        title="Back to Projects"
      >
        <NavLink to={backHref}>
          <ArrowLeftIcon />
          <span className="sr-only">Back to Projects</span>
        </NavLink>
      </Button>
      <ProjectIcon displayName={displayName} iconUrl={iconUrl} size="sm" />
      <p className="truncate text-sm font-medium text-foreground" title={displayName}>
        {displayName}
      </p>
    </div>
  );
}

/**
 * The pinned header: brand, then where you are, the workspace and, inside a
 * project, the project. Its rows are as short as their controls, because
 * every pixel it keeps is one the navigation below it loses.
 */
export function AppSidebarHeader({
  workspace,
  project,
}: {
  readonly workspace: ReactNode;
  readonly project: ReactNode;
}) {
  return (
    <SidebarHeader className="gap-1 pb-0">
      <SidebarBrand />
      <div className="flex flex-col gap-1 group-data-[collapsible=icon]:hidden">
        {workspace}
        <SidebarSeparator className="mx-0" />
        {project}
      </div>
    </SidebarHeader>
  );
}

function NavItems({
  items,
  isActive,
}: {
  readonly items: readonly NavItem[];
  readonly isActive: (item: NavItem) => boolean;
}) {
  return (
    <SidebarMenu>
      {items.map((item) => {
        const active = isActive(item);
        const { onPopInEnd } = item;
        return (
          <SidebarMenuItem
            key={`${item.label}-${item.href}`}
            className={onPopInEnd === undefined ? undefined : 'ca-capability-pop-in'}
            onAnimationEnd={
              onPopInEnd === undefined
                ? undefined
                : (event) => {
                    if (
                      event.target === event.currentTarget &&
                      event.animationName === 'ca-capability-flash'
                    ) {
                      onPopInEnd();
                    }
                  }
            }
          >
            {active ? (
              <span
                className="pointer-events-none absolute top-2 bottom-2 left-0 w-0.5 rounded-full bg-sidebar-primary"
                aria-hidden="true"
              />
            ) : null}
            <SidebarMenuButton
              asChild
              isActive={active}
              tooltip={item.label}
              className={cn(
                'group-data-[collapsible=icon]:size-11! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:p-0! group-data-[collapsible=icon]:[&>span:last-child]:hidden',
                active &&
                  'bg-sidebar-primary/8 text-sidebar-foreground font-medium data-active:bg-sidebar-primary/8 data-active:text-sidebar-foreground',
              )}
            >
              <NavLink
                aria-label={item.label}
                to={item.href}
                {...(item.folioTargetId === undefined
                  ? {}
                  : { 'data-folio-target': item.folioTargetId })}
              >
                <item.icon />
                <span>{item.label}</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}

export function SidebarNavSection({
  ariaLabel,
  items,
  isActive,
  label,
}: {
  readonly ariaLabel: string;
  readonly items: readonly NavItem[];
  readonly isActive: (item: NavItem) => boolean;
  readonly label: string;
}) {
  if (items.length === 0) {
    return null;
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="sr-only">{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <nav aria-label={ariaLabel}>
          <NavItems items={items} isActive={isActive} />
        </nav>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

/** A project's primary action; folded, it is an icon button the size of the rail's nav buttons. */
export function SidebarUploadAction({ href }: { readonly href: string }) {
  return (
    <SidebarGroup>
      <SidebarGroupLabel className="sr-only">Project actions</SidebarGroupLabel>
      <SidebarGroupContent>
        <div className="px-2 group-data-[collapsible=icon]:px-0">
          <Button
            asChild
            variant="default"
            size="lg"
            className="w-full justify-start group-data-[collapsible=icon]:size-11! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0!"
          >
            <NavLink to={href} title="Upload documents">
              <UploadIcon data-icon="inline-start" />
              <span className="group-data-[collapsible=icon]:sr-only">Upload documents</span>
            </NavLink>
          </Button>
        </div>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

/** The user menu's trigger: avatar and name, folding to the avatar alone. */
export function SidebarUserMenuButton({
  displayName,
  photoUrl,
  role,
  ...props
}: Omit<ComponentProps<typeof SidebarMenuButton>, 'children' | 'size'> & {
  readonly displayName: string;
  readonly photoUrl: string | null;
  readonly role: string;
}) {
  return (
    <SidebarMenuButton
      {...props}
      className="group-data-[collapsible=icon]:size-11! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:p-0!"
      size="lg"
      aria-label={`User menu for ${displayName}`}
      data-folio-target="sidebar-user-menu-trigger"
    >
      <Avatar className="size-8">
        {photoUrl !== null ? (
          <AvatarImage alt={displayName} src={photoUrl} />
        ) : null}
        <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
      </Avatar>
      <div className="flex flex-col gap-0.5 leading-none group-data-[collapsible=icon]:hidden">
        <span className="text-sm font-medium">{displayName}</span>
        <span className="text-xs text-muted-foreground">{role}</span>
      </div>
    </SidebarMenuButton>
  );
}

/**
 * The scrolling body: the navigation, then the details at its foot, in the
 * order they give way: the agent invitation, usage, the copyright line. With
 * room to spare the details sit on the footer; on a short window they follow
 * the navigation and scroll, so they give up their space before any
 * navigation does, the copyright first and the invitation's action last.
 */
export function AppSidebarBody({
  children,
  agentInvitation,
  usage,
}: {
  readonly children: ReactNode;
  readonly agentInvitation: ReactNode;
  readonly usage: ReactNode;
}) {
  return (
    <SidebarContent>
      {children}
      <div className="mt-auto flex flex-col gap-2 p-2">
        {agentInvitation}
        {usage}
        <p className="px-2 text-xs text-balance text-muted-foreground group-data-[collapsible=icon]:hidden">
          © 2026 EYGM Limited. All rights reserved.
        </p>
      </div>
    </SidebarContent>
  );
}

/**
 * The pinned footer: the user menu, with the connected agent's mark beside it.
 * Folded, the mark stacks above the avatar.
 */
export function AppSidebarFooter({
  agentStatus,
  userMenu,
}: {
  readonly agentStatus: ReactNode;
  readonly userMenu: ReactNode;
}) {
  return (
    <SidebarFooter className="gap-1 overflow-x-hidden pt-0">
      <SidebarSeparator className="mx-0" />
      <div className="flex items-center gap-1 group-data-[collapsible=icon]:flex-col-reverse">
        <nav aria-label="User menu" className="min-w-0 flex-1">
          <SidebarMenu>
            <SidebarMenuItem>{userMenu}</SidebarMenuItem>
          </SidebarMenu>
        </nav>
        {agentStatus}
      </div>
    </SidebarFooter>
  );
}
